/**
 * GAP 5b — advisory findings as a REPORT artifact (design §4: advisory policy
 * findings are a report surface, NOT a diagnostic stream). `policyDiagnostics`
 * deliberately drops `severity: advisory` findings; this module surfaces them for
 * the advisory panel in the details/inspector area.
 *
 * Pure over the in-browser resolved model + the workspace governance closure, so
 * it is testable headless (no DOM). The `useAdvisoryFindings` hook is a thin
 * `useMemo` wrapper over this.
 */
import {
  advisoryFindings,
  buildClosure,
  buildNodeUniverse,
  buildProcessLatencyResolver,
  buildProcessTestResolver,
  type DependencyGraph,
  type EvalContext,
  type Finding,
  type ResolvedModel,
} from "@archlang/engine";
import type { Vfs } from "@archlang/viewer-core";
import { collectViewGovernance } from "./viewPlan.js";

/** One advisory finding shaped for the report panel: a stable key, the policy
 *  that raised it, and its report message (mirrors the diagnostic-stream wording). */
export interface AdvisoryEntry {
  readonly anchorKey: string;
  readonly policy: string;
  readonly message: string;
  readonly sort: "node" | "edge";
}

/** The report message for a finding — mirrors `advisory-report.ts`'s wording. */
export function advisoryMessage(f: Finding): string {
  return f.ruleKind === "forbid"
    ? `${f.anchorKey} is forbidden by policy ${f.policy}`
    : `${f.anchorKey} violates policy ${f.policy} (required relationship missing)`;
}

/**
 * Every active advisory-severity policy finding over the workspace, deterministic
 * order (the engine sorts by anchor source, then message). Empty when there are
 * no policies / no advisory findings, or the model has not loaded. Never throws —
 * an un-evaluable policy is skipped (`onError`), a build error yields an empty
 * report rather than taking down the panel.
 */
export function computeAdvisoryFindings(
  resolvedModel: ResolvedModel | null,
  graph: DependencyGraph | null,
  sources: Vfs | null,
): AdvisoryEntry[] {
  if (!resolvedModel || !graph) return [];
  const governance = collectViewGovernance(resolvedModel, sources);
  if (!governance || governance.policyList.length === 0) return [];
  try {
    const ctx: EvalContext = {
      universe: buildNodeUniverse(resolvedModel),
      graph,
      closure: buildClosure(graph),
      policies: governance.policies,
      queries: governance.queries,
      presence: resolvedModel.presence,
      processLatency: buildProcessLatencyResolver(resolvedModel),
      processTest: buildProcessTestResolver(resolvedModel),
    };
    return advisoryFindings(governance.policyList, ctx, { onError: () => undefined }).map((f) => ({
      anchorKey: f.anchorKey,
      policy: f.policy,
      message: advisoryMessage(f),
      sort: f.sort,
    }));
  } catch {
    return [];
  }
}
